import React, { useState, useEffect } from "react";
import ApiService from "../../services/Api.service";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Alert from "../Alert/Alert";
import Pagination from "../Pagination/Pagination";
import StatusBadge from "../../utilities/StatusBadge";
import FilterBar from "../FilterBar/FilterBar";
import Loader from "../Loader/Loader";
import TableSubSection from "./TableSubSection";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import BulkRecrodingJobs from "./BulkRecrodingJobs";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import VoiceAnalyticsAiModel from "../VoiceAnalyticsPopUpModel/VoiceAnalyticsAiModel";
import { Eye, RotateCw } from "lucide-react";

export default function AllCalls() {
    const [Loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [sections, setSections] = useState([]);
    const [postsPerPage, setPostsPerPage] = useState(20);
    const [headers, setHeaders] = useState([]);
    const [dropDownData, setDropDownData] = useState([]);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [dispositionOptions, setDispositionOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);

    const [activeCallId, setActiveCallId] = useState(null);
    const [activeTab, setActiveTab] = useState("All Calls");
    const tabs = ["All Calls", "Download Jobs"];
    const [lastFilters, setLastFilters] = useState({});


    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        total_pages: 0,
    });
    const [alertValue, setAlertValue] = useState({
        show: false,
        message: "",
        variant: "success",
        position: "middle",
    });
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
    const [selectedAnalyticsItem, setSelectedAnalyticsItem] = useState(null);
    const [syncingCallId, setSyncingCallId] = useState(null);
    const [cqaEnabled, setCqaEnabled] = useState(false);

    const handleOpenAnalytics = (item, e) => {
        if (e) e.stopPropagation();
        setSelectedAnalyticsItem(item);
        setShowAnalyticsModal(true);
    };

    const handleSyncToCqa = async (item, e) => {
        if (e) e.stopPropagation();
        const callId = item.id || item._id;
        if (!callId) return;

        try {
            setSyncingCallId(callId);
            const res = await ApiService.syncCallToCqa(callId);
            if (res && res.status === 200) {
                setAlertValue({
                    show: true,
                    message: res.data?.message || "Call successfully synced to CQA! Audit In-Process.",
                    variant: "success",
                    position: "middle",
                });
                setTableData((prev) =>
                    prev.map((c) =>
                        (c.id === callId || c._id === callId)
                            ? { ...c, audit_status: res.data?.cqa_status || "In-Process", audit_call_id: res.data?.audit_call_id }
                            : c
                    )
                );
            } else {
                setAlertValue({
                    show: true,
                    message: res.data?.detail || "Failed to sync call to CQA",
                    variant: "danger",
                    position: "middle",
                });
            }
        } catch (err) {
            console.error("handleSyncToCqa error:", err);
            setAlertValue({
                show: true,
                message: err.response?.data?.detail || "Failed to sync call to CQA",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setSyncingCallId(null);
            setTimeout(() => {
                setAlertValue({ show: false, message: "", variant: "" });
            }, 3000);
        }
    };

    // minimal validation (kept empty as original)
    const validationSchema = yup.object().shape({});

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            campaign_id: "",
            agents: [],
            products: [],
            search: "",
            start_date: "",
            end_date: "",
        },
    });

    // fetch campaign options
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const campaignRes = await ApiService.getCampaignOptions();
            const campaignData = campaignRes?.data?.data || [];
            setCampaignOptions(campaignData);
            const disResponse = await ApiService.getDispositionOptions();
            const disData = disResponse?.data?.data || [];
            setDispositionOptions(disData)
            const statusOptionsRes = await ApiService.getCallStatusOptions();
            const statusData = statusOptionsRes?.data?.data || [];
            setStatusOptions(statusData)
        } catch (err) {
            console.error("Error loading dashboard data:", err);
        } finally {
            setLoading(false);
        }
    };

    // fetch dropdown (subprocesses)
    const fetchDropdownDetail = async () => {
        try {
            const subprocessRes = await ApiService.getSubprocesses();
            if (subprocessRes.status === 200) setDropDownData(subprocessRes.data);
        } catch (error) {
            console.error(error);
        }
    };

    // main detail fetch with proper loading handling
    const fetchDetail = async (page = 1, size = postsPerPage, payload = lastFilters) => {
        try {
            setLoading(true);
            if (!payload) {
                payload = {
                    campaign_id: "",
                };
            }
            setLastFilters(payload);
            const res = await ApiService.getAllCallList(payload, { page, size });
            if (res.status === 200 && res.data) {
                setTableData(res.data.data || []);
                setPagination(res.data.pagination || { page, size, total: 0, total_pages: 0 });
                const receivedHeaders = res.data.header || [];
                const isCqa = Boolean(res.data.cqa_enabled || receivedHeaders.some((h) => h.value === "action" || h.value === "audit_status"));
                setCqaEnabled(isCqa);
                setHeaders(receivedHeaders.filter((h) => h.value !== "action"));
                setSections(res.data.sections || []);
                setSelectedRowIndex(null);
            } else {
                // If non-200, clear table
                setTableData([]);
                setHeaders([]);
                setSections([]);
                setCqaEnabled(false);
            }
        } catch (error) {
            console.error("fetchDetail error:", error);
        } finally {
            setLoading(false);
            setActiveCallId(null);
        }
    };

    // Combined initial effect to avoid duplicate calls
    useEffect(() => {
        fetchDropdownDetail();
        fetchDashboardData();
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handelSubmit = async (data) => {
        // Build payload from filter values (ignore empty strings and empty arrays)
        const payload = Object.fromEntries(
            Object.entries(data).filter(([_, v]) => {
                if (Array.isArray(v)) return v.length > 0;
                return v !== "" && v !== null && v !== undefined;
            })
        );
        fetchDetail(1, postsPerPage, payload);
    };

    const handleClearFilters = () => {
        reset({ campaign_id: "", agents: [], products: [], search: "", date_from: "" });
        fetchDetail();
        setActiveCallId(null);
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPostsPerPage(newSize);
        fetchDetail(1, newSize);
        setSelectedRowIndex(null);
        setActiveCallId(null);
    };

    const mistableTh = {
        backgroundColor: "transparent",
        color: "rgba(43, 58, 103, 1)",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "0.1em",
        textAlign: "center",
    };

    const tableTdStyles = {
        backgroundColor: "#F7F7F7",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: "700",
        lineHeight: "16px",
        letterSpacing: "0em",
        textAlign: "center",
        color: "#232122",
    };

    // FilterBar fields (wired to campaignOptions loaded from API)
    const filterDefs = [
        { type: "text", name: "search", placeholder: "Search..." },
        { type: "select", name: "campaign_id", placeholder: "Campaign", options: campaignOptions },
        { type: "multiselect", name: "disposition", placeholder: "Disposition - All", options: dispositionOptions },
        { type: "multiselect", name: "status", placeholder: "Status - All", options: statusOptions },
        { type: "date", name: "start_date", placeholder: "Start Date" },
        { type: "date", name: "end_date", placeholder: "End Date" },
    ];

    const handleAudioDownload = () => {
        createAudioDownloadJob(lastFilters);
    };

    const createAudioDownloadJob = async (data) => {
        try {
            const subprocessRes = await ApiService.createRecordingJob(data);
            if (subprocessRes && (subprocessRes.status === 200 || subprocessRes.status === 201)) {
                setAlertValue({
                    show: true,
                    message: "Audio download job created successfully.",
                    variant: "success",
                    position: "middle",
                });
            } else {
                setAlertValue({

                    show: true,
                    message: "Failed to create audio download job.",
                    variant: "danger",
                    position: "middle",
                });
            }
        } catch (error) {
            console.error("createAudioDownloadJob error:", error);
            setAlertValue({
                show: true,
                message: error.response?.data?.detail || "Failed to create user",
                variant: "danger",
                position: "middle",
            });
        } finally {
            // auto-dismiss alert after a short timeout
            setTimeout(() => {
                setAlertValue({ show: false, message: "", variant: "" });
            }, 2500);
        }
    };

    const confirmDeleteCalls = async () => {
        try {
            setLoading(true);
            const res = await ApiService.deleteCalls(lastFilters);
            if (res && (res.status === 200 || res.status === 201)) {
                setAlertValue({
                    show: true,
                    message: res.data?.message || "Delete job created successfully.",
                    variant: "success",
                    position: "middle",
                });
                await fetchDetail();
            } else {
                setAlertValue({
                    show: true,
                    message: "Failed to create delete job.",
                    variant: "danger",
                    position: "middle",
                });
            }
        } catch (error) {
            console.error("deleteCalls error:", error);
            setAlertValue({
                show: true,
                message: error.response?.data?.detail || "Failed to create delete job",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setTimeout(() => {
                setAlertValue({ show: false, message: "", variant: "" });
            }, 2500);
        }
    };

    return (
        <>
            {alertValue.show && (
                <Alert
                    variant={alertValue.variant}
                    message={alertValue.message}
                    dismissible={true}
                    timeout={2500}
                    position={alertValue.position}
                    onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
                />
            )}

            <div className="main-contanier">
                <div className="bulk-download-call-tabs">
                    {tabs.map((tab) => (
                        <div
                            key={tab}
                            className={`tab ${activeTab === tab ? "active" : ""}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </div>
                    ))}
                </div>

                {activeTab === "All Calls" && (
                    <>
                        <FilterBar
                            control={control}
                            handleSubmit={handleSubmit}
                            onSubmit={handelSubmit}
                            onClearFilters={handleClearFilters}
                            filters={filterDefs}
                            title={"All Calls"}
                            totalCount={pagination.total || 0}
                            tableId="table-id"
                            isDownload={false}
                            isDownloadAudio={true}
                            hendleDownloadAudio={handleAudioDownload}
                            isServerExport={true}
                            isDelete={true}
                            onDelete={() => setShowDeleteModal(true)}
                        />

                        <div className="table-container" style={{ height: "calc(100vh - 210px)", position: "relative" }}>
                            {Loading ? (
                                <SkeletonLoader type="table" rows={8} cols={5} />
                            ) : (
                                <table id="table-id" className="custom-table-container">
                                    <thead className="table-head" style={{ position: "sticky", top: "0", zIndex: "1" }}>
                                        <tr>
                                            <th style={mistableTh}>S.No</th>
                                            {headers.map((header, i) => (
                                                <th key={i} style={mistableTh}>
                                                    {header.label}
                                                </th>
                                            ))}
                                            <th style={mistableTh}>Recording</th>
                                            {cqaEnabled && <th style={mistableTh}>Action</th>}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {tableData && tableData.length > 0 ? (
                                            tableData.map((item, index) => (
                                                <React.Fragment key={index}>
                                                    <tr
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setSelectedRowIndex(selectedRowIndex === index ? null : index)}
                                                    >
                                                        <td
                                                            style={{
                                                                ...tableTdStyles,
                                                                backgroundColor: selectedRowIndex === index ? "#f9faff" : "transparent",
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </td>
                                                        {headers.map((header, i) => (
                                                            <td
                                                                key={i}
                                                                style={{
                                                                    ...tableTdStyles,
                                                                    backgroundColor: selectedRowIndex === index ? "#f9faff" : "transparent",
                                                                }}
                                                                title={item[header.value] ?? "--"}
                                                            >
                                                                {header.value === "status" ? (
                                                                    <StatusBadge status={item.status} />
                                                                ) : header.value === "audit_status" ? (
                                                                    <StatusBadge status={item.audit_status} />
                                                                ) : header.value === "disposition" ? (
                                                                    <StatusBadge status={item.disposition} />
                                                                ) : (
                                                                    <span className="cell-ellipsis">{item[header.value] ?? "--"}</span>
                                                                )}
                                                            </td>
                                                        ))}
                                                        <td
                                                            style={{
                                                                ...tableTdStyles,
                                                                backgroundColor: selectedRowIndex === index ? "#f9faff" : "transparent",
                                                            }}
                                                        >
                                                            <AudioPlayer
                                                                callId={item.id}
                                                                duration={item.duration}
                                                                activeCallId={activeCallId}
                                                                setActiveCallId={setActiveCallId}
                                                            />
                                                        </td>
                                                        {cqaEnabled && (
                                                            <td
                                                                style={{
                                                                    ...tableTdStyles,
                                                                    backgroundColor: selectedRowIndex === index ? "#f9faff" : "transparent",
                                                                    textAlign: "center"
                                                                }}
                                                            >
                                                                <div style={{ display: "inline-flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                                                                    <button
                                                                        type="button"
                                                                        title="View Voice Analytics & CQA"
                                                                        onClick={(e) => handleOpenAnalytics(item, e)}
                                                                        style={{
                                                                            background: "#e0effe",
                                                                            border: "1px solid #bfdbfe",
                                                                            color: "#1d4ed8",
                                                                            cursor: "pointer",
                                                                            padding: "6px 10px",
                                                                            borderRadius: "6px",
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            transition: "all 0.2s"
                                                                        }}
                                                                    >
                                                                        <Eye size={16} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        title="Sync / Inject Call to CQA"
                                                                        onClick={(e) => handleSyncToCqa(item, e)}
                                                                        disabled={syncingCallId === (item.id || item._id)}
                                                                        style={{
                                                                            background: "#ecfdf5",
                                                                            border: "1px solid #a7f3d0",
                                                                            color: "#059669",
                                                                            cursor: syncingCallId === (item.id || item._id) ? "not-allowed" : "pointer",
                                                                            padding: "6px 10px",
                                                                            borderRadius: "6px",
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            transition: "all 0.2s",
                                                                            opacity: syncingCallId === (item.id || item._id) ? 0.7 : 1
                                                                        }}
                                                                    >
                                                                        <RotateCw
                                                                            size={16}
                                                                            style={{
                                                                                animation: syncingCallId === (item.id || item._id) ? "spin 1s linear infinite" : "none"
                                                                            }}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>

                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={headers.length + (cqaEnabled ? 3 : 2)} style={tableTdStyles}>
                                                    No data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <Pagination
                            pagination={pagination}
                            postsPerPage={postsPerPage}
                            handlePageSizeChange={handlePageSizeChange}
                            fetchDetail={fetchDetail}
                        />
                    </>
                )}

                {activeTab === "Download Jobs" && <BulkRecrodingJobs />}
            </div>

            {showDeleteModal && (
                <ConfirmDeleteModal
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={confirmDeleteCalls}
                    itemName={`all calls matching current filters (${pagination.total || 0} total)`}
                />
            )}

            {showAnalyticsModal && (
                <VoiceAnalyticsAiModel
                    showModal={showAnalyticsModal}
                    closeModal={() => setShowAnalyticsModal(false)}
                    item={selectedAnalyticsItem}
                />
            )}
        </>
    );
}
